import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import bank from 'Pages/help/components/accountSidebar/accountHero/assets/bank.svg';
import calendar from 'Pages/help/components/accountSidebar/accountHero/assets/calendar.svg';
import star from 'Pages/help/components/accountSidebar/accountHero/assets/star.svg';
import { avatarColorSchemes } from 'Pages/help/components/accountSidebar/accountHero/constants';
import {
  AccountHeroAvatar,
  AccountHeroAvatarWrapper,
  AccountHeroDate,
  AccountHeroName,
  AccountHeroStats,
  AccountHeroStatsList,
  AccountHeroStatsListItem,
  AccountHeroStatsListItemIcon,
  AccountHeroStatsListItemLabel,
  AccountHeroStatsListItemValue,
  AccountHeroStatsListWrapper,
  AccountHeroWrapper
} from 'Pages/help/components/accountSidebar/accountHero/style';
import { getCurrencySymbol } from 'Shared/components/design/currencySymbol/CurrencySymbol';
import { roundToDecimalPlaces } from 'Shared/services/helperService';
import { useGetUserStats } from 'Shared/services/red/api/redAPI';
import { capitalize } from 'Shared/utils/formatUtils';

interface AccountHeroProps {
  user: {
    id                   ?: number;
    firstName            ?: string;
    lastName             ?: string;
    createdAt            ?: Date;
    lifetimePoints       ?: number;
    pointsToCurrencyRatio?: number;
  }
}

const currentDateTime    = new Date();
const beginningOfTheYear = new Date(currentDateTime.getFullYear(), 0, 1);

/**
 * Compute an index for selecting an avatar color scheme based on the user ID.
 *
 * @param {string} userId - The unique identifier for a user.
 * @param {number} colorSchemesCount - The total number of available color schemes.
 * @return {number} The computed index within the range of available color schemes.
 */
const computeAvatarColorIndexBasedOnUserId = (userId: string, colorSchemesCount: number): number => {
  if (!userId) {
    return -1;
  }
  const sumOfChars = Array.from(userId).reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return sumOfChars % colorSchemesCount;
};

/**
 * Renders the Account Hero component
 * @param user
 * @returns JSX.Element
 */
const AccountHero = ({ user }: AccountHeroProps): JSX.Element => {
  const { t }               = useTranslation('accountHelp');
  const { data: userStats } = useGetUserStats(beginningOfTheYear.toISOString(), currentDateTime.toISOString());
  const currencySymbol      = getCurrencySymbol();
  const redeemedAmount      = userStats?.pointsRedeemed && user.pointsToCurrencyRatio ? roundToDecimalPlaces(Math.abs(userStats.pointsRedeemed)/user.pointsToCurrencyRatio, 2) : 0;

  /**
   * Renders the user initials
   * @returns JSX.Element | string
   */
  const userInitials = useMemo((): JSX.Element | string => {
    if (!user.firstName || !user.lastName) {
      return '-';
    }

    return `${capitalize(user.firstName)} ${capitalize(user.lastName[0])}.`;
  }, [user]);

  /**
   * Renders the avatar initials
   * @returns JSX.Element | string
   */
  const avatarInitials = useMemo((): JSX.Element | string => {
    if (!user.firstName || !user.lastName) {
      return '-';
    }

    return `${capitalize(user.firstName)[0]}${capitalize(user.lastName[0])}`;
  }, [user]);

  /**
   * The color scheme to use for the avatar.
   */
  const avatarColorScheme = useMemo(() => {
    if (!user.id) {
      return;
    }
    const index = computeAvatarColorIndexBasedOnUserId(String(user.id), avatarColorSchemes.length);
    return avatarColorSchemes[index];
  }, [user]);

  /**
   * Renders the user creation date
   * @returns JSX.Element | string
   */
  const renderCreatedDate = (): JSX.Element | string => {
    if (!user.createdAt) {
      return '-';
    }

    return `${t('AccountSidebar--hero-memberSince')} ${new Date(user.createdAt).getFullYear()}`;
  };

  /**
   * List of stats to display
   * @type {{label: string; value: string; icon: string}[]}
   */
  const statsList: { label: string; value: string; icon: string; }[] = [
    {
      label: t('AccountSidebar--hero-lifetimePoints'),
      value: user.lifetimePoints?.toLocaleString('en-US') ?? '-',
      icon : star
    },
    {
      label: t('AccountSidebar--hero-earnedYTD'),
      value: userStats?.pointsEarned.toLocaleString('en-US') ?? '-',
      icon : calendar
    },
    {
      label: t('AccountSidebar--hero-redeemedYTD'),
      value: `${currencySymbol}${redeemedAmount}`,
      icon : bank
    }
  ];

  return (
    <AccountHeroWrapper>
      <AccountHeroAvatarWrapper>
        <AccountHeroAvatar colorScheme={avatarColorScheme}>
          {avatarInitials}
        </AccountHeroAvatar>
      </AccountHeroAvatarWrapper>
      <AccountHeroName>{userInitials}</AccountHeroName>
      <AccountHeroDate>
        {renderCreatedDate()}
      </AccountHeroDate>
      <AccountHeroStats>
        <AccountHeroStatsListWrapper>
          <AccountHeroStatsList>
            {statsList.map((item) => {
              return (
                <AccountHeroStatsListItem key={item.label}>
                  <AccountHeroStatsListItemLabel>
                    <AccountHeroStatsListItemIcon src={item.icon} />
                    {item.label}
                  </AccountHeroStatsListItemLabel>
                  <AccountHeroStatsListItemValue>
                    {item.value}
                  </AccountHeroStatsListItemValue>
                </AccountHeroStatsListItem>
              );
            })}
          </AccountHeroStatsList>
        </AccountHeroStatsListWrapper>
      </AccountHeroStats>
    </AccountHeroWrapper>
  );
};

export default AccountHero;